import { useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { ArrowUpRight, Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Overline, Reveal } from "@/components/motion";

const APP_URL = "https://app.uzakyonetim.com.tr";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const inputCls =
  "w-full rounded-xl border border-black/10 bg-[#FAFAFA] px-5 py-3.5 text-sm text-[#0A0A0A] placeholder:text-[#9CA3AF] outline-none transition-colors duration-300 focus:border-[#003366]/40 focus:bg-white";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [token, setToken] = useState(null);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const turnstileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!token) {
      setStatus({ ok: false, text: "Lütfen spam doğrulamasını tamamlayın." });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, turnstile_token: token }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Gönderim başarısız oldu.");
      setForm({ name: "", email: "", message: "" });
      setStatus({ ok: true, text: body.message });
    } catch (err) {
      setStatus({ ok: false, text: err.message });
    } finally {
      setSending(false);
      setToken(null);
      turnstileRef.current?.reset();
    }
  };

  return (
    <section id="iletisim" data-testid="contact-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.1fr] lg:items-start">
          <div>
            <Reveal>
              <Overline>İletişim</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-bold tracking-tighter text-[#0A0A0A] sm:text-5xl lg:text-6xl">
                Altyapınızı
                <br />
                konuşalım.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#525252] md:text-lg">
                Kurulum, lisanslama veya teknik destek için formu doldurun.
                Ekibimiz en kısa sürede size dönüş yapacaktır.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-12 rounded-3xl bg-[#003366] p-8 md:p-10">
                <h3 className="font-display text-xl font-bold tracking-tight text-white md:text-2xl">
                  Hesabınız var mı?
                </h3>
                <p className="mt-2 text-sm text-white/70">
                  Yönetim paneline giriş yaparak cihazlarınızı hemen yönetmeye başlayın.
                </p>
                <a
                  href={APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-login-button"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#003366] transition-transform duration-300 hover:scale-[1.03]"
                >
                  app.uzakyonetim.com.tr
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-3xl border border-black/5 bg-white p-8 md:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-[#525252]">
                    Ad Soyad
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Adınız Soyadınız"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-[#525252]">
                    E-posta
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="ornek@sirket.com"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-[#525252]">
                  Mesajınız
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  minLength={10}
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Size nasıl yardımcı olabiliriz?"
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div className="mt-6">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={SITE_KEY}
                  options={{ action: "contact", theme: "light" }}
                  onSuccess={setToken}
                  onExpire={() => setToken(null)}
                  onError={() => setToken(null)}
                />
              </div>

              {status && (
                <p
                  data-testid="contact-form-status"
                  role="status"
                  className={`mt-5 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
                    status.ok
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {status.ok ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  ) : (
                    <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  )}
                  {status.text}
                </p>
              )}

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending || !token}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#003366] px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-[#336699] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} />
                    Gönderiliyor…
                  </>
                ) : (
                  <>
                    Mesajı Gönder
                    <Send className="h-4 w-4" strokeWidth={1.5} />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
